import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Tooltip, Button, Spin, Avatar, Tag, Dropdown, Switch, message } from "antd";
import {
  ShopOutlined, UserOutlined, QuestionCircleOutlined,
  ClockCircleOutlined, StarFilled, StarOutlined,
  EditOutlined, PlusOutlined, CloseCircleOutlined,
  PushpinFilled, PushpinOutlined, CalendarOutlined,
} from "@ant-design/icons";
import { API_DOMAIN } from "../constants";
import { AuthContext } from "../contexts/AuthContext";
import { TICKET_STATUS, TICKET_STATUS_STRING } from "./constants";
import { expiryDisplay, getCardCountry, CopyablePhone } from "./helpers";
import { ct } from "./MessageParts";
import { AgentDropdown } from "./AgentDropdown";
import { CustomerEditDrawer } from "./CustomerEditDrawer";

export function ChatPanelHeader({ chat, pinned, onPinToggle, onClose, isMobile, onChatPatch, isDark, onThemeToggle, imageMessages = [], onQueryCreated }) {
  const theme = ct(isDark ?? true);
  const tsCfg = chat?.ticket_status != null ? TICKET_STATUS[chat.ticket_status] : null;
  const { authState } = useContext(AuthContext);

  const [customer, setCustomer] = useState(null);
  const [localType, setLocalType] = useState(null);
  const [typeLoading, setTypeLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editDefaultTab, setEditDefaultTab] = useState("customer");
  const typeInFlight = useRef(false);

  useEffect(() => {
    if (!chat?.phone) { setCustomer(null); return; }
    setCustomer(null);
    const headers = { accessToken: localStorage.getItem("accessToken") };
    axios.get(API_DOMAIN + "/customers/get_full_profile", { params: { phone: chat.phone }, headers })
      .then(res => {
        if (res.data.success && res.data.data?.length > 0) {
          setCustomer(res.data.data[0]);
        } else {
          axios.post(API_DOMAIN + "/whatsapp/customer_add", { name: chat.customer_name || chat.phone, number: chat.phone }, { headers })
            .then(() => {
              message.success("Saved the customer automatically");
              axios.get(API_DOMAIN + "/customers/get_full_profile", { params: { phone: chat.phone }, headers })
                .then(r => { if (r.data.success && r.data.data?.length > 0) setCustomer(r.data.data[0]); })
                .catch(() => { });
            }).catch(() => { });
        }
      }).catch(() => { });
  }, [chat?.phone]); // eslint-disable-line

  useEffect(() => {
    setLocalType(customer?.type ?? null);
  }, [customer]);

  const handleTypeChange = async (val) => {
    if (!customer?.customer_id || typeInFlight.current) return;
    typeInFlight.current = true;
    const prev = customer?.type ?? null;
    setLocalType(val ?? null);
    setTypeLoading(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/customers/update",
        { customer_id: customer.customer_id, type: val ?? null },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data.success) throw new Error(res.data.message);
      setCustomer(c => ({ ...c, type: val ?? null }));
      onChatPatch?.({ customer_type: val ?? null });
    } catch {
      setLocalType(prev);
      message.error("Failed to update customer type");
    } finally {
      setTypeLoading(false);
      typeInFlight.current = false;
    }
  };

  const handleCustomerSaved = (updatedCustomer) => {
    if (!updatedCustomer) return;
    setCustomer(updatedCustomer);
    setLocalType(updatedCustomer?.type ?? null);
    onChatPatch?.({ customer_type: updatedCustomer.type ?? null });
  };

  const handleStatusChange = async (statusStr) => {
    const ticket = chat?.raw?.ticket;
    if (!ticket || statusLoading) return;
    setStatusLoading(true);
    try {
      const res = await axios.post(
        API_DOMAIN + "/wati_messages/change_chat_status",
        {
          conversationId: ticket.conversationId,
          status: statusStr,
          lastAssignedAgentId: ticket.lastAssignedAgentId || null,
          ticketId: ticket.id,
          userId: authState.id,
        },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data?.success) throw new Error(res.data?.message);
      const numericStatus = { Open: 1, Pending: 3, Solved: 2 }[statusStr] ?? null;
      onChatPatch?.({ ticket_status: numericStatus });
    } catch (e) {
      message.error(e.message || "Failed to change status");
    } finally {
      setStatusLoading(false);
    }
  };

  const toggleStar = async () => {
    const newStarred = !chat.starred;
    onChatPatch?.({ starred: newStarred });
    try {
      const res = await axios.post(
        API_DOMAIN + "/wati_messages/toggle_star_chat",
        { conversationId: chat.conversationId, userId: authState.id },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (!res.data?.success) throw new Error();
    } catch {
      onChatPatch?.({ starred: !newStarred });
    }
  };

  const currentStatusStr = TICKET_STATUS_STRING[chat?.ticket_status ?? -1];
  const isSolved = chat?.ticket_status === 2;
  const statusMenuItems = [
    { key: "Open", label: <span style={{ color: TICKET_STATUS[1].color, fontWeight: 600 }}>Open</span> },
    ...(!isSolved ? [
      { key: "Pending", label: <span style={{ color: TICKET_STATUS[3].color, fontWeight: 600 }}>Pending</span> },
      { key: "Solved", label: <span style={{ color: TICKET_STATUS[2].color, fontWeight: 600 }}>Solved</span> },
    ] : []),
  ].map(i => i.key === currentStatusStr
    ? { ...i, disabled: true, label: <span style={{ color: "#bfbfbf", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>{i.label}<span style={{ fontSize: 10, color: "#bfbfbf", fontWeight: 400 }}>· current</span></span> }
    : i
  );

  const customerType = customer?.type ?? chat?.customer_type;
  const avatarIcon = customerType === 0 ? <ShopOutlined /> : customerType === 1 ? <UserOutlined /> : <QuestionCircleOutlined />;
  const avatarColor = customerType === 0 ? "#1677ff" : customerType === 1 ? "#722ed1" : "#d48806";

  return (
    <>
      <div className="cpt" style={{ display: "flex", alignItems: "stretch", gap: 8, width: "100%" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
          <Avatar size={32} icon={avatarIcon} style={{ backgroundColor: avatarColor, flexShrink: 0 }} />
          <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: 3, width: 32 }}>
            <Tooltip title="Edit customer profile" placement="right" getPopupContainer={n => n.parentElement ?? document.body}>
              <Button
                disabled={!customer}
                icon={<EditOutlined style={{ fontSize: 12 }} />}
                onClick={() => { setEditDefaultTab("customer"); setEditOpen(true); }}
                style={{ width: 32, flex: 1, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", color: theme.textSub, borderColor: theme.border, background: "transparent" }}
              />
            </Tooltip>
            <Tooltip title="Create Query" placement="right" getPopupContainer={n => n.parentElement ?? document.body}>
              <Button
                disabled={!customer}
                icon={<PlusOutlined style={{ fontSize: 12 }} />}
                onClick={() => { setEditDefaultTab("queries"); setEditOpen(true); }}
                style={{ width: 32, flex: 1, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,168,132,0.12)", borderColor: "#00a884", color: "#00a884" }}
              />
            </Tooltip>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textAlign: "left" }}>
          <div style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: theme.text }}>
            {chat?.customer_name || chat?.phone || "Unknown"}
          </div>
          {customer?.customer_real_name && (
            <div style={{ fontSize: 11, color: theme.textSub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 1 }}>
              {customer.customer_real_name}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
            {(() => {
              const info = getCardCountry(chat?.phone);
              return info ? (
                <Tooltip title={info.name} getPopupContainer={n => n.parentElement ?? document.body}>
                  <img src={`https://cdn.clare.ai/flags/${info.code}.svg`} alt={info.name}
                    style={{ width: 14, height: 14, objectFit: "contain", borderRadius: 2, flexShrink: 0, cursor: "default" }} />
                </Tooltip>
              ) : null;
            })()}
            {chat?.phone && (
              <span style={{ fontSize: 11, color: "#8696a0", flexShrink: 0 }}>
                <CopyablePhone phone={chat.phone} />
              </span>
            )}
            {customer?.create_date && (
              <Tag style={{ fontSize: 10, margin: 0, padding: "0 5px", flexShrink: 0, color: theme.textSub, border: `1px solid ${theme.border}`, background: theme.pill, display: "inline-flex", alignItems: "center", gap: 3 }}>
                <CalendarOutlined style={{ fontSize: 9 }} />
                Since {dayjs(customer.create_date).format("MMM YYYY")}
              </Tag>
            )}
            <Dropdown
              trigger={["click"]}
              disabled={statusLoading || (chat?.expiry?.isValid() && chat.expiry.isBefore(dayjs()))}
              menu={{ items: statusMenuItems, onClick: ({ key }) => handleStatusChange(key) }}
              dropdownRender={isDark ? menu => React.cloneElement(menu, { className: `${menu.props?.className || ""} qv4-dark-menu`.trim(), style: { ...(menu.props?.style || {}), background: "#1f2c34", border: "1px solid #2a3942", boxShadow: "0 6px 20px rgba(0,0,0,0.5)" } }) : undefined}
            >
              <Tag style={{ fontSize: 10, margin: 0, padding: "0 5px", cursor: statusLoading ? "wait" : "pointer", fontWeight: 600, flexShrink: 0, userSelect: "none", color: tsCfg ? tsCfg.color : theme.textSub, background: tsCfg ? tsCfg.bg : theme.pill, border: tsCfg ? `1px solid ${tsCfg.border}` : `1px solid ${theme.border}` }} onClick={e => e.stopPropagation()}>
                {statusLoading ? <Spin size="small" /> : <>{tsCfg ? tsCfg.label : "Status"} ▾</>}
              </Tag>
            </Dropdown>
          </div>
          <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {chat?.expiry?.isValid() && (() => {
              const exp = expiryDisplay(chat.expiry);
              const isExpired = chat.expiry.isBefore(dayjs());
              let expLabel;
              if (isExpired) {
                const minsAgo = dayjs().diff(chat.expiry, "minute");
                if (minsAgo < 60) expLabel = `${minsAgo}m ago`;
                else if (minsAgo < 1440) { const h = Math.floor(minsAgo / 60); expLabel = `${h}h ago`; }
                else { const d = Math.floor(minsAgo / 1440); expLabel = `${d}d ago`; }
              } else {
                expLabel = chat.expiry.isSame(dayjs(), "day")
                  ? chat.expiry.format("hh:mm A")
                  : `tomorrow ${chat.expiry.format("hh:mm A")}`;
              }
              return (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 8px", borderRadius: 6, border: `1px solid ${exp.color}`, background: theme.pill, flexShrink: 0 }}>
                  <ClockCircleOutlined style={{ fontSize: 11, color: exp.color }} />
                  <span style={{ fontSize: 11, color: exp.color, fontWeight: 600 }}>{exp.label} · {expLabel}</span>
                </div>
              );
            })()}
            {(() => {
              const typeCfg = localType === 0
                ? { label: "B2B", color: "#1677ff", bg: "#e6f4ff", border: "#91caff" }
                : localType === 1
                  ? { label: "B2C", color: "#722ed1", bg: "#f9f0ff", border: "#d3adf7" }
                  : null;
              const typeMenuItems = [
                { key: "0", label: <span style={{ color: "#1677ff", fontWeight: 600 }}>B2B</span> },
                { key: "1", label: <span style={{ color: "#722ed1", fontWeight: 600 }}>B2C</span> },
                { type: "divider" },
                { key: "clear", label: <span style={{ color: "#8c8c8c" }}>Clear</span> },
              ];
              return (
                <Dropdown
                  trigger={["click"]}
                  disabled={!customer || typeLoading}
                  menu={{ items: typeMenuItems, onClick: ({ key }) => handleTypeChange(key === "clear" ? null : Number(key)) }}
                  dropdownRender={isDark ? menu => React.cloneElement(menu, { className: `${menu.props?.className || ""} qv4-dark-menu`.trim(), style: { ...(menu.props?.style || {}), background: "#1f2c34", border: "1px solid #2a3942", boxShadow: "0 6px 20px rgba(0,0,0,0.5)" } }) : undefined}
                >
                  <Tag
                    onClick={e => e.stopPropagation()}
                    style={{
                      fontSize: 10, margin: 0, padding: "0 5px", flexShrink: 0, userSelect: "none",
                      cursor: (!customer || typeLoading) ? "not-allowed" : "pointer", fontWeight: 600,
                      color: typeCfg ? typeCfg.color : theme.textSub,
                      background: typeCfg ? typeCfg.bg : theme.pill,
                      border: typeCfg ? `1px solid ${typeCfg.border}` : `1px solid ${theme.border}`,
                    }}
                  >
                    {typeLoading ? <Spin size="small" /> : (typeCfg ? typeCfg.label : "Type")} ▾
                  </Tag>
                </Dropdown>
              );
            })()}
            <Tooltip title={chat.starred ? "Unstar" : "Star"} getPopupContainer={n => n.parentElement ?? document.body}>
              <span
                onClick={toggleStar}
                style={{ cursor: "pointer", color: chat.starred ? "#faad14" : theme.border, fontSize: 15, display: "flex", alignItems: "center" }}
              >
                {chat.starred ? <StarFilled /> : <StarOutlined />}
              </span>
            </Tooltip>
          </div>
          <div style={{ marginTop: 3 }}>
            <AgentDropdown chat={chat} isDark={isDark} onAssigned={fields => onChatPatch?.(fields)} />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0, alignSelf: "flex-start" }}>
          {!pinned && <Tooltip title="Close" getPopupContainer={n => n.parentElement ?? document.body}><Button type="text" size="small" icon={<CloseCircleOutlined />} onClick={onClose} style={{ color: "#8696a0" }} /></Tooltip>}
          {!isMobile && (
            <Tooltip title={pinned ? "Unpin" : "Pin to sidebar"} getPopupContainer={n => n.parentElement ?? document.body}>
              <Button type={pinned ? "primary" : "text"} size="small" icon={pinned ? <PushpinFilled style={{ color: pinned ? undefined : "#8696a0" }} /> : <PushpinOutlined style={{ color: "#8696a0" }} />} onClick={onPinToggle} />
            </Tooltip>
          )}
          <Tooltip title={isDark ? "Switch to light mode" : "Switch to dark mode"} placement="left" getPopupContainer={n => n.parentElement ?? document.body}>
            <Switch
              size="small"
              checked={isDark}
              onChange={onThemeToggle}
              checkedChildren="D"
              unCheckedChildren="L"
              style={{ marginTop: 2 }}
            />
          </Tooltip>
        </div>
      </div>
      <CustomerEditDrawer
        customer={customer}
        open={editOpen}
        defaultTab={editDefaultTab}
        onClose={() => setEditOpen(false)}
        onSaved={handleCustomerSaved}
        imageMessages={imageMessages}
        onQueryCreated={onQueryCreated}
      />
    </>
  );
}
